/**
 * frontend/src/pages/valuation/_steps.ts
 *
 * VAL-PATH-ROUTES (P1) — the one step list. The left nav, the step bar, the
 * dashboard card and every screen's Back/Next read it, so they cannot
 * disagree. The owner walks six steps, the signer four; admin gets the signer
 * list and may still open any owner step (D4).
 *
 * Every done-test reads only the server summary fields on the engagement
 * (company_done, years_enough, applied, questions_done, value_ready, status) —
 * never session state (U3). A missing field counts as not done. The current
 * step (D1, P2b) is the step after the furthest step done — the first step on
 * a new engagement, the last when all are done — so a step left open earlier
 * does not pull a returning user back. Nothing stores it: every device
 * computes the same step from the saved data.
 */
import type { ValuationEngagement } from '@/types/valuation'

export const LOCKED_MESSAGE = 'Enter or upload at least two years of financials to continue'
// VAL-PATH-FOOTER (P2a): the footer's two moves (owner-approved 2026-10-20).
export const SAVE_AND_CONTINUE = 'Save and continue'
export const BACK = 'Back'

export type StepRole = 'owner' | 'signer' | 'admin'
export type PathStepKey =
  | 'company' | 'financials' | 'adjust' | 'intake' | 'output' | 'signoff'
  | 'review' | 'tuning' | 'report'
/** A route segment the guard can be asked about. */
export type GuardStep = PathStepKey | 'overview'

export interface PathStep {
  key: PathStepKey
  label: string
  /** U9: one line under the title saying what the screen is for (owner-approved 10-22). */
  purpose?: string
  route: (id: string) => string
  done: (e: ValuationEngagement) => boolean
}

const at = (seg: string) => (id: string) => `/valuation/${id}/${seg}`
// The two-year minimum lives on the server (step facts `years_enough`); no TS copy.
const yearsOk = (e: ValuationEngagement) => !!e.years_enough
const signed = (e: ValuationEngagement) => e.status === 'signed_off'

const OWNER_STEPS: PathStep[] = [
  {
    key: 'company',
    label: 'Company',
    purpose: 'Tell us about the business and what this valuation is for.',
    route: at('company'),
    done: e => !!e.company_done,
  },
  {
    key: 'financials',
    label: 'Financials',
    // POL-B: owner-approved 2026-10-23 13:53 CT (replaces the 10-22 05:43 U9 line).
    purpose: 'Upload your profit and loss statements or enter your figures yourself, for at least two years, then check the figures.',
    route: at('financials'),
    done: yearsOk,
  },
  {
    key: 'adjust',
    label: 'Adjustments and rates',
    purpose: 'Add back owner and one-off costs and enter the balance sheet. Your value updates as you save; the rates are set by your valuation signer.',
    route: at('adjust'),
    done: e => !!e.applied,
  },
  {
    key: 'intake',
    label: 'Questions',
    purpose: 'Answer 25 questions about how the business runs - 11 are required. They place your value within the industry range.',
    route: at('intake'),
    done: e => !!e.questions_done,
  },
  {
    key: 'output',
    label: 'Your value',
    purpose: 'Your calculated value today, its range, and how it was built.',
    route: at('output'),
    done: e => !!e.value_ready,
  },
  {
    key: 'signoff',
    label: 'Get your report',
    purpose: 'Choose the Snapshot, the full signed report, or both. Nothing needs to be entered again.',
    route: at('signoff'),
    done: signed,
  },
]

const SIGNER_STEPS: PathStep[] = [
  {
    key: 'review',
    label: 'Review',
    purpose: 'Check everything the owner entered, step by step. Open any step to correct it.',
    route: at('review'),
    done: e => !!e.company_done && yearsOk(e) && !!e.applied && !!e.questions_done,
  },
  {
    key: 'tuning',
    label: 'Methodology and rates',
    purpose: 'Set the weighting, rates and any override. The value updates as you change them.',
    route: at('tuning'),
    done: e => !!e.value_ready,
  },
  {
    key: 'signoff',
    label: 'Sign',
    purpose: 'Confirm your declarations, give one reason, and sign this record.',
    route: at('signoff'),
    done: signed,
  },
  {
    key: 'report',
    label: 'Full report',
    purpose: 'The signed report. Produce, download or email it.',
    route: id => `/valuation/${id}/signoff#report`,
    done: () => false,
  },
]

// Owner steps 3-6 need two years of financials read first (D3; WALKFIX-B F15,
// owner ruling 10-22 21:28 adds Adjustments).
const LOCKABLE: readonly PathStepKey[] = ['adjust', 'intake', 'output', 'signoff']

export function stepRole(role: string | undefined): StepRole {
  if (role === 'super_admin') return 'admin'
  if (role === 'valuation_signer') return 'signer'
  return 'owner'
}

export function stepsFor(role: string | undefined): PathStep[] {
  return stepRole(role) === 'owner' ? OWNER_STEPS : SIGNER_STEPS
}

export function currentStep(e: ValuationEngagement, role: string | undefined): PathStep {
  const steps = stepsFor(role)
  let furthest = -1
  steps.forEach((s, i) => { if (s.done(e)) furthest = i })
  const resume = steps[furthest + 1] ?? steps[steps.length - 1]
  // Never resume on a locked step (D3): the first step not done is the one
  // that unlocks it, so the guard does not bounce between the two.
  if (isLocked(resume.key, e, role)) return steps.find(s => !s.done(e)) ?? resume
  return resume
}

export function isLocked(key: PathStepKey, e: ValuationEngagement | null, role: string | undefined): boolean {
  if (!e || stepRole(role) !== 'owner') return false
  return LOCKABLE.includes(key) && !yearsOk(e)
}

// A key outside the role's list (a signer on an owner screen, or a signer
// step with no role known) moves along the list that has it. WALKFIX-C F6:
// the step bar shows that same list, so staff on an owner step see the
// owner's bar. A key in neither list ('profile') keeps the role's list.
export function stepsAt(key: string, role: string | undefined): PathStep[] {
  const steps = stepsFor(role)
  if (steps.some(s => s.key === key)) return steps
  if (OWNER_STEPS.some(s => s.key === key)) return OWNER_STEPS
  if (SIGNER_STEPS.some(s => s.key === key)) return SIGNER_STEPS
  return steps
}

const orderFor = stepsAt

export function stepNext(key: PathStepKey, role: string | undefined, id: string): string | null {
  const steps = orderFor(key, role)
  const next = steps[steps.findIndex(s => s.key === key) + 1]
  return next ? next.route(id) : null
}

export function stepBack(key: PathStepKey, role: string | undefined, id: string): string | null {
  const steps = orderFor(key, role)
  const i = steps.findIndex(s => s.key === key)
  return i > 0 ? steps[i - 1].route(id) : null
}

/** Where the guard sends this person instead of `step`, or null to open it. */
export function guardRedirect(
  step: GuardStep,
  role: string | undefined,
  e: ValuationEngagement | null,
  id?: string,
): string | null {
  const r = stepRole(role)
  const eid = e?.valuation_engagement_id ?? id ?? ''
  if (r === 'owner' && step === 'tuning') return at('output')(eid)
  if (step === 'overview') {
    if (r !== 'owner') return at('review')(eid)
    return e ? currentStep(e, role).route(eid) : null
  }
  if (r !== 'owner' || !e) return null
  if (step === 'review') return currentStep(e, role).route(eid)
  if (step !== 'report' && isLocked(step, e, role)) return currentStep(e, role).route(eid)
  return null
}
